// authService.js
import axios from "axios";

import { BASE_URL } from "./apiBase";

const AUTH_BASE_URL = `${BASE_URL}/api/Auth`;
const USERS_BASE_URL = `${BASE_URL}/api/Users`;

export const getSocialProviders = async () => (await axios.get(`${AUTH_BASE_URL}/external/providers`)).data;
export const getSocialLogins = async () => (await axios.get(`${AUTH_BASE_URL}/external/logins`)).data;
export const getSocialRegistration = async () => (await axios.get(`${AUTH_BASE_URL}/external/registration`)).data;
export const registerSocialUser = async (formData, registrationKey) => (await axios.post(`${AUTH_BASE_URL}/external/register`, {
  userRole: formData.userRole, firstName: formData.firstName, lastName: formData.lastName, registrationKey,
  businessName: formData.businessName || null, businessDescription: formData.businessDescription || null,
  profilePictureUrl: formData.profilePictureUrl || null, businessBannerPicUrl: formData.businessBannerPicUrl || null,
})).data;

const handleError = (label, error) => {
  console.error(`${label} error:`, error);
  throw error;
};

export const registerClient = async (payload) => {
  try {
    const response = await axios.post(
      `${AUTH_BASE_URL}/register-client`,
      payload
    );
    return response.data;
  } catch (error) {
    handleError("Registration", error);
  }
};

export const registerVillager = async (payload) => {
  try {
    const response = await axios.post(
      `${AUTH_BASE_URL}/register-villager`,
      payload
    );
    return response.data;
  } catch (error) {
    handleError("Registration", error);
  }
};

export const registerUser = async (formData) => {
  const payload = {
    email: formData.email,
    password: formData.password,
    firstName: formData.firstName,
    lastName: formData.lastName,
    profilePictureUrl: formData.profilePictureUrl || null,
    businessName: formData.businessName,
    businessDescription: formData.businessDescription,
    businessBannerPicUrl: formData.businessBannerPicUrl,
  };

  if (formData.userRole === "Villager") {
    return registerVillager(payload);
  }
  return registerClient(payload);
};

export const loginUser = async (credentials) => {
  try {
    const response = await axios.post(`${AUTH_BASE_URL}/login`, credentials);
    return response.data;
  } catch (error) {
    handleError("Login", error);
  }
};

export const requestPasswordReset = async (email) => {
  // SECURITY: the reset token is a bearer credential. Callers must NOT render
  // any `token` field from this response in the UI — the reset link is emailed.
  // Backend should stop returning the token in this response entirely.
  try {
    const response = await axios.post(`${AUTH_BASE_URL}/request-password-reset`, {
      email,
    });
    return response.data;
  } catch (error) {
    handleError("Password reset request", error);
  }
};

export const resetPassword = async (payload) => {
  try {
    const response = await axios.post(`${AUTH_BASE_URL}/reset-password`, payload);
    return response.data;
  } catch (error) {
    handleError("Password reset", error);
  }
};

export const requestEmailConfirmation = async (email) => {
  try {
    const response = await axios.post(
      `${AUTH_BASE_URL}/request-email-confirmation`,
      { email }
    );
    return response.data;
  } catch (error) {
    handleError("Email confirmation request", error);
  }
};

export const confirmEmail = async (payload) => {
  try {
    const response = await axios.post(`${AUTH_BASE_URL}/confirm-email`, payload);
    return response.data;
  } catch (error) {
    handleError("Confirm email", error);
  }
};

export const getCurrentUser = async () => {
  try {
    const response = await axios.get(`${USERS_BASE_URL}/me`);
    return response.data;
  } catch (error) {
    handleError("Profile", error);
  }
};

/** Update the signed-in user's own details. Email is not editable — changing it would
 *  invalidate the confirmed-email state and the login identity. */
export const updateCurrentUser = async (payload) => {
  const response = await axios.put(`${USERS_BASE_URL}/me`, payload);
  return response.data;
};

/** Change the password while signed in. Distinct from the emailed reset flow, which is for
 *  people who cannot sign in at all. */
export const changePassword = async ({ currentPassword, newPassword }) => {
  const response = await axios.post(`${USERS_BASE_URL}/me/change-password`, {
    currentPassword,
    newPassword,
  });
  return response.data;
};

export const logoutUser = async () => {
  try {
    await axios.post(`${AUTH_BASE_URL}/logout`);
  } catch {
    // Ignore — cookie will expire naturally
  }
};
